import React, { useState, useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown } from 'react-icons/fa';
import './PanelToggles.css';

/**
 * Compact panel visibility control: one trigger opens a menu of checkboxes.
 * @param {Array<{key: string, label: React.ReactNode, checked: boolean, onChange: (checked: boolean) => void}>} items
 */
export default function PanelToggles({ items }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!dropdownOpen || !buttonRef.current) {
      setMenuPosition(null);
      return;
    }

    const handleClickOutside = (e) => {
      if (
        !buttonRef.current?.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };
    const handleScrollOrResize = () => setDropdownOpen(false);
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 8,
      left: rect.left,
      minWidth: Math.max(rect.width, 200),
    });
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      setMenuPosition(null);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [dropdownOpen]);

  const renderToggle = (item) => (
    <label
      key={item.key}
      className={`toggle-checkbox ${item.checked ? 'checked' : ''}`}
    >
      <input
        type="checkbox"
        checked={item.checked}
        onChange={(e) => item.onChange(e.target.checked)}
      />
      <span className="toggle-label">{item.label}</span>
    </label>
  );

  if (items.length === 0) return null;

  return (
    <div className="panel-toggles-wrapper">
      <button
        ref={buttonRef}
        type="button"
        className="panel-toggles-dropdown-btn"
        onClick={() => setDropdownOpen((o) => !o)}
        title="Show or hide panels"
        aria-expanded={dropdownOpen}
        aria-haspopup="true"
        aria-controls={dropdownOpen ? menuId : undefined}
      >
        Panels
        <FaChevronDown
          size={12}
          style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'none' }}
        />
      </button>
      {dropdownOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="group"
            aria-label="Visible panels"
            className="panel-toggles-dropdown-menu"
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              minWidth: menuPosition.minWidth,
            }}
          >
            {items.map((item) => renderToggle(item))}
          </div>,
          document.body
        )}
    </div>
  );
}
